import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, Btn, Badge, Modal, Field, Input, Select } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { toast } from "sonner";
import {
  CaretLeft, CaretRight, ArrowLeft, Plus, X, LockKey, Calendar, ChartPieSlice, Info, CheckCircle
} from "@phosphor-icons/react";

// ---------------------------------------------------------------- constants
const STATUS_CELL_CLASS = {
  present:         "bg-emerald-50/80 border-emerald-500/30 text-emerald-800 shadow-xs shadow-emerald-100/50",
  leave:           "bg-slate-100 border-slate-500/30 text-slate-800 shadow-xs shadow-slate-100/50",
  half_day:        "bg-amber-50/80 border-amber-500/30 text-amber-800 shadow-xs shadow-amber-100/50",
  special_holiday: "bg-indigo-50/80 border-indigo-500/30 text-indigo-800 shadow-xs shadow-indigo-100/50",
  // legacy (rendered on historical rows)
  absent:          "bg-rose-50 border-rose-400 text-rose-800",
  paid_leave:      "bg-slate-100 border-slate-500/30 text-slate-800",
  casual_leave:    "bg-slate-100 border-slate-500/30 text-slate-800",
  sick:            "bg-slate-100 border-slate-500/30 text-slate-800",
  casual:          "bg-slate-100 border-slate-500/30 text-slate-800",
};

const STATUS_LETTER = {
  present: "P", leave: "L", half_day: "HD", special_holiday: "SH",
  // legacy
  absent: "A", paid_leave: "L", casual_leave: "L", sick: "L", casual: "L",
};

const STATUS_LABEL = {
  present: "Present", leave: "Leave", half_day: "Half day",
  special_holiday: "Special holiday",
  // legacy
  absent: "Absent", paid_leave: "Leave", casual_leave: "Leave",
};

const EDIT_OPTIONS_BASE = [
  { key: "present",  label: "Present",  letter: "P",  cls: "bg-emerald-600 hover:bg-emerald-700 shadow-sm ring-offset-2 focus:ring-2 ring-emerald-500" },
  { key: "leave",    label: "Leave",    letter: "L",  cls: "bg-slate-700 hover:bg-slate-800 shadow-sm ring-offset-2 focus:ring-2 ring-slate-600" },
  { key: "half_day", label: "Half day", letter: "HD", cls: "bg-amber-600 hover:bg-amber-700 shadow-sm ring-offset-2 focus:ring-2 ring-amber-500" },
];

const SPECIAL_HOLIDAY_OPTION = {
  key: "special_holiday", label: "Sp. Holiday", letter: "SH", cls: "bg-indigo-600 hover:bg-indigo-700 shadow-sm ring-offset-2 focus:ring-2 ring-indigo-500",
};

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ---------------------------------------------------------------- page
export default function EmployeeAttendance() {
  const { eid } = useParams();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "production";
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/attendance/employee/${eid}`, { params: { year, month } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setBusy(false); }
  }, [eid, year, month]);

  useEffect(() => { load(); }, [load]);

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const markDay = useCallback(async (dateStr, status, existingRecId) => {
    if (!canEdit) return;
    if (!data) return;
    try {
      if (status === null) {
        if (!existingRecId) return;
        await api.delete(`/attendance/${existingRecId}`);
        toast.success(`Cleared ${dateStr}`);
      } else {
        await api.post(`/attendance`, {
          worker_name: data.employee.name,
          date: dateStr,
          status,
          notes: "",
        });
        toast.success(`${STATUS_LABEL[status]} · ${dateStr}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  }, [canEdit, data, load]);

  const monthName = useMemo(() => new Date(year, month - 1, 1).toLocaleString("en-US", {
    month: "long", year: "numeric",
  }), [year, month]);

  const calendar = useMemo(() => {
    if (!data) return [];
    const cells = [];
    const first = new Date(year, month - 1, 1);
    let leading = (first.getDay() + 6) % 7;
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= data.last_day; d++) {
      const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const rec = data.by_date?.[dateStr];
      const dow = new Date(year, month - 1, d).getDay();
      cells.push({ day: d, dateStr, rec, isSunday: dow === 0 });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [data, year, month]);

  if (!data) {
    return (
      <div className="p-6 lg:p-10 flex items-center justify-center min-h-[300px]">
        <div className="text-sm font-mono text-slate-400 flex items-center gap-3 bg-slate-50 border border-slate-200 px-4 py-3 rounded-xl shadow-inner">
          <div className={`w-2 h-2 rounded-full ${busy ? "bg-amber-500 animate-pulse" : "bg-slate-300"}`} />
          {busy ? "Syncing timeline parameters..." : "No asset data verified"}
        </div>
      </div>
    );
  }

  const s = data.stats || {};
  const emp = data.employee || {};
  const todayISO = today.toISOString().slice(0, 10);
  const isAdmin = user?.role === "admin";
  const editOptions = isAdmin ? [...EDIT_OPTIONS_BASE, SPECIAL_HOLIDAY_OPTION] : EDIT_OPTIONS_BASE;
  const payableDays = ((s.present || 0) + (s.special_holiday || 0)
                        + (s.half_day || 0) * 0.5 + (s.leave || 0)).toFixed(1);
  const attendancePct = data.working_days > 0
    ? Math.round(((s.present || 0) + (s.special_holiday || 0)) / data.working_days * 100)
    : 0;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-5" data-testid="emp-attendance-page">
      <PageHeader
        label="WORKFORCE · EMPLOYEE ATTENDANCE"
        title={emp.name}
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-slate-600 font-medium">{emp.role || "employee"} · <code className="text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{emp.emp_id || "—"}</code></span>
            {!canEdit && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-slate-100 border border-slate-200 text-slate-500 px-2 py-0.5 rounded-md">
                <LockKey size={12} className="text-slate-400" /> Read only
              </span>
            )}
          </span>
        }
        action={
          <div className="flex gap-2">
            {canEdit && (
              <Btn onClick={() => setRangeOpen(true)} data-testid="mark-range-btn" className="shadow-sm hover:translate-y-[-1px] transition-transform">
                <Plus size={14} weight="bold" /> Mark Range
              </Btn>
            )}
            <Link to="/attendance">
              <Btn variant="secondary" data-testid="back-to-attendance" className="border-slate-200 hover:bg-slate-50 text-slate-700">
                <ArrowLeft size={14} weight="bold" /> Back
              </Btn>
            </Link>
          </div>
        }
      />

      <div className="bg-white border border-slate-200/80 rounded-2xl p-3 shadow-sm backdrop-blur-md flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-xl border border-slate-200/50">
          <button onClick={() => shiftMonth(-1)} data-testid="prev-month"
            className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg transition-all active:scale-95" aria-label="Previous month">
            <CaretLeft size={14} weight="bold" />
          </button>
          <div className="font-sans text-sm font-bold text-slate-800 min-w-[140px] text-center" data-testid="month-label">
            {monthName}
          </div>
          <button onClick={() => shiftMonth(1)} data-testid="next-month"
            className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg transition-all active:scale-95" aria-label="Next month">
            <CaretRight size={14} weight="bold" />
          </button>
        </div>
        <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); }}
          data-testid="jump-today"
          className="text-xs font-semibold uppercase tracking-wider text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100/70 px-3 py-1.5 rounded-xl transition-all">
          Jump to today
        </button>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm space-y-4" data-testid="month-summary">
        <div className="flex items-start justify-between border-b border-slate-100 pb-3 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <ChartPieSlice size={14} className="text-slate-500" /> Executive Metrics Overview
            </div>
          </div>
          <div className="text-right bg-slate-50 border border-slate-100 px-3 py-1 rounded-xl flex items-center gap-3">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ratio:</span>
            <div className={`font-mono font-black text-xl tracking-tight ${attendancePct >= 90 ? "text-emerald-600" : attendancePct >= 70 ? "text-amber-600" : "text-rose-600"}`}
                 data-testid="attendance-pct">
              {attendancePct}%
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Working days" value={data.working_days} sub={`${data.last_day} days total`} icon="scale" />
          <StatCard label="Marked / Unmarked" value={`${data.marked_days} / ${data.unmarked_days}`} accent={data.unmarked_days > 0 ? "red" : "green"} icon="status" />
          <StatCard label="Payable days" value={payableDays} sub="P + SH + L + ½HD" accent="orange" icon="payroll" />
          <StatCard label="Present Basis" value={s.present || 0} accent="green" icon="check" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-slate-100 pt-3">
          <StatCard label="Special holiday" value={s.special_holiday || 0} accent="indigo" variant="compact" />
          <StatCard label="Leaves" value={s.leave || 0} accent="slate" variant="compact" />
          <StatCard label="Half days" value={s.half_day || 0} accent="amber" variant="compact" />
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm" data-testid="attendance-calendar">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
              <Calendar size={15} weight="bold" />
            </div>
            <div className="text-sm font-bold text-slate-800 leading-tight">{monthName}</div>
          </div>
        </div>

        {/* Dense week row header */}
        <div className="grid grid-cols-7 gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
          {WEEKDAY_LABELS.map(d => (
            <div key={d} className={`px-1 text-center py-1 bg-slate-50/60 rounded-md ${d === "Sun" ? "text-rose-500 bg-rose-50/40" : ""}`}>
              {d}
            </div>
          ))}
        </div>

        {/* Compact core date tile grid layout */}
        <div className="grid grid-cols-7 gap-1">
          {calendar.map((cell, idx) => {
            if (!cell) return <div key={`blank-${idx}`} className="h-16 bg-slate-50/20 rounded-lg border border-dashed border-slate-100" />;
            return (
              <DayCell
                key={cell.dateStr}
                cell={cell}
                canEdit={canEdit && !cell.isSunday}
                editOptions={editOptions}
                isToday={cell.dateStr === todayISO}
                onMark={(status) => markDay(cell.dateStr, status, cell.rec?.id)}
              />
            );
          })}
        </div>
        <Legend />
      </div>

      {rangeOpen && (
        <MarkRangeModal
          empName={emp.name}
          isAdmin={isAdmin}
          onClose={() => setRangeOpen(false)}
          onSaved={() => { setRangeOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------- Day cell (with edit popover)
function DayCell({ cell, canEdit, editOptions, isToday, onMark }) {
  const [open, setOpen] = useState(false);
  const rec = cell.rec;
  const letter = rec ? STATUS_LETTER[rec.status] || "?" : (cell.isSunday ? "H" : "");
  
  const bg = rec
    ? `${STATUS_CELL_CLASS[rec.status]} border`
    : cell.isSunday
      ? "bg-rose-50/40 border-rose-200/60 text-rose-600 font-medium"
      : "bg-white border-slate-200 text-slate-400 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-600";

  const label = rec
    ? STATUS_LABEL[rec.status] || rec.status.replace("_", " ")
    : cell.isSunday ? "Sunday" : "";

  const cellContent = (
    <div
      data-testid={`cal-${cell.dateStr}`}
      title={canEdit ? `${label || "Not marked"} — click to change` : label}
      className={
        `relative h-16 border rounded-lg p-1.5 flex flex-col justify-between text-[10px] ` +
        `transition-all duration-150 ease-out select-none group ${bg} ` +
        `${isToday ? "ring-2 ring-amber-500 ring-offset-1 border-amber-400 z-10" : ""} ` +
        `${canEdit ? "cursor-pointer hover:shadow-xs hover:scale-[1.01]" : "cursor-default"}`
      }
    >
      <div className="flex items-center justify-between leading-none">
        <span className="font-extrabold text-slate-800 text-xs">{cell.day}</span>
        {isToday && (
          <span className="text-[7px] font-sans font-bold uppercase tracking-tight text-amber-800 bg-amber-100 px-1 rounded-sm">Now</span>
        )}
      </div>
      <div className="text-center font-sans font-black text-sm leading-none tracking-tight py-0.5">{letter}</div>
      <div className="text-[7px] font-sans font-bold uppercase tracking-tight text-center opacity-70 truncate max-w-full">
        {rec ? label : cell.isSunday ? "Sun" : ""}
      </div>
    </div>
  );

  if (!canEdit) return cellContent;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="p-0 m-0 text-left w-full block focus:outline-none" data-testid={`cal-btn-${cell.dateStr}`}>
          {cellContent}
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-56 p-3 rounded-xl border-slate-200/80 shadow-lg backdrop-blur-md z-50" data-testid={`edit-pop-${cell.dateStr}`}>
        <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
          <Info size={12} className="text-slate-400" /> Target: {cell.dateStr}
        </div>
        <div className="grid grid-cols-1 gap-1">
          {editOptions.map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={async () => { setOpen(false); await onMark(opt.key); }}
              data-testid={`mark-${opt.key}-${cell.dateStr}`}
              className={`text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg flex items-center justify-between transition-all active:scale-98 ${opt.cls}`}
            >
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 bg-white/20 rounded-sm flex items-center justify-center text-[9px] font-black">{opt.letter}</span>
                {opt.label}
              </span>
            </button>
          ))}
        </div>
        {rec && (
          <button
            type="button"
            onClick={async () => { setOpen(false); await onMark(null); }}
            data-testid={`mark-clear-${cell.dateStr}`}
            className="w-full mt-2 border border-slate-200 text-slate-600 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg flex items-center justify-center gap-1.5 bg-slate-50 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-100 transition-all"
          >
            <X size={11} weight="bold" /> Clear Parameter
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ------------------------------------------------------ Multi-day mark modal
function MarkRangeModal({ empName, isAdmin, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [status, setStatus] = useState("present");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const dates = React.useMemo(() => {
    if (!from || !to || from > to) return [];
    const out = [];
    let d = new Date(from + "T00:00:00");
    const end = new Date(to + "T00:00:00");
    while (d <= end) {
      out.push(d.toISOString().slice(0, 10));
      d.setDate(d.getDate() + 1);
    }
    return out;
  }, [from, to]);

  const sundays = dates.filter(d => new Date(d + "T00:00:00").getDay() === 0).length;
  const workingDates = dates.length - sundays;

  const submit = async (e) => {
    e.preventDefault();
    if (dates.length === 0) { toast.error("Pick a valid date range"); return; }
    setBusy(true);
    try {
      const r = await api.post("/attendance/range", {
        worker_name: empName, dates, status, notes: notes.trim(),
      });
      toast.success(`Saved · ${r.data.created} new + ${r.data.updated} updated`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Mark Range · ${empName}`} className="max-w-md rounded-2xl">
      <form onSubmit={submit} className="space-y-4 p-0.5" data-testid="range-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="From date">
            <DatePicker value={from} onChange={e => setFrom(e.target.value)} required data-testid="range-from" className="rounded-xl border-slate-200" />
          </Field>
          <Field label="To date">
            <DatePicker value={to} min={from} onChange={e => setTo(e.target.value)} required data-testid="range-to" className="rounded-xl border-slate-200" />
          </Field>
        </div>
        <Field label="Status Type">
          <Select value={status} onChange={e => setStatus(e.target.value)} data-testid="range-status" className="rounded-xl border-slate-200 h-10">
            <option value="present">Present</option>
            <option value="leave">Leave</option>
            <option value="half_day">Half day</option>
            {isAdmin && <option value="special_holiday">Special holiday</option>}
          </Select>
        </Field>
        <Field label="Notes (optional)">
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reason / notes" className="rounded-xl border-slate-200 h-10" />
        </Field>

        <div className="bg-slate-50 border border-slate-200/60 p-3 rounded-xl text-xs font-mono grid grid-cols-3 gap-2" data-testid="range-preview">
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Days Span</div>
            <div className="text-base font-black text-slate-800 mt-0.5">{dates.length}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Net Working</div>
            <div className="text-base font-black text-amber-600 mt-0.5">{workingDates}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Exempt</div>
            <div className="text-base font-black text-slate-400 mt-0.5">{sundays}</div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <Btn type="button" variant="secondary" onClick={onClose} className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</Btn>
          <Btn type="submit" disabled={busy || workingDates === 0} data-testid="range-submit" className="rounded-xl px-4 shadow-sm">
            {busy ? "Saving…" : `Mark ${workingDates} day(s)`}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- helpers
function StatCard({ label, value, sub, accent, variant = "standard" }) {
  const accentCls = {
    green: "text-emerald-600 bg-emerald-50 border-emerald-100",
    red: "text-rose-600 bg-rose-50 border-rose-100",
    orange: "text-amber-600 bg-amber-50 border-amber-100",
    yellow: "text-yellow-600 bg-yellow-50 border-yellow-100",
    zinc: "text-slate-600 bg-slate-50 border-slate-100",
    indigo: "text-indigo-600 bg-indigo-50 border-indigo-100",
    slate: "text-slate-700 bg-slate-100 border-slate-200",
  }[accent] || "text-slate-800 bg-slate-50/50 border-slate-100";

  if (variant === "compact") {
    return (
      <div className="border border-slate-100 bg-slate-50/40 p-2 rounded-xl flex items-center justify-between transition-all hover:bg-white hover:border-slate-200">
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <span className={`font-mono font-black text-sm px-2 py-0.5 rounded-lg border ${accentCls}`}>{value}</span>
      </div>
    );
  }

  return (
    <div className="border border-slate-200/70 bg-white p-3 rounded-xl shadow-sm transition-all hover:border-slate-300 flex flex-col justify-between group">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
        <div className="font-mono font-black text-xl tracking-tight mt-0.5 text-slate-800">{value}</div>
      </div>
      {sub && <div className="text-[9px] font-sans font-medium text-slate-400 mt-1 border-t border-slate-50 pt-1">{sub}</div>}
    </div>
  );
}

function Legend() {
  const items = [
    ["P",  "Present",         "bg-emerald-50 border-emerald-500/30 text-emerald-700"],
    ["PL", "Paid leave",      "bg-slate-100 border-slate-500/30 text-slate-700"],
    ["CL", "Casual leave",    "bg-amber-50 border-amber-500/30 text-amber-700"],
    ["SH", "Special holiday", "bg-indigo-50 border-indigo-500/30 text-indigo-700"],
    ["H",  "Sunday holiday",  "bg-rose-50/50 border-rose-200 text-rose-600"],
  ];
  return (
    <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">
      {items.map(([k, label, cls], i) => (
        <div key={`${k}-${i}`} className="flex items-center gap-1 bg-slate-50/60 border border-slate-100 px-2 py-0.5 rounded-lg">
          <div className={`w-4 h-4 border rounded-sm font-sans text-[9px] font-black flex items-center justify-center ${cls}`}>{k}</div>
          <span className="text-[10px] font-sans font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        </div>
      ))}
    </div>
  );
}